import React, { useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { LevelInput } from '../../api/levelsApi';

interface CreateLevelModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: LevelInput) => Promise<void>;
    /** Floor numbers already used by other levels in the building. */
    existingNumbers: number[];
    submitting?: boolean;
}

interface CreateLevelFormValues {
    name: string;
    numberText: string;
    ceilingHeightMm: string;
    knownAreaText: string;
}

const initialValues: CreateLevelFormValues = {
    name: '',
    numberText: '',
    ceilingHeightMm: '',
    knownAreaText: '',
};

const toInput = (values: CreateLevelFormValues): LevelInput => ({
    name: values.name.trim() || undefined,
    number: Number(values.numberText),
    ceilingHeight: values.ceilingHeightMm.trim() ? Number(values.ceilingHeightMm) / 1000 : undefined,
    knownArea: values.knownAreaText.trim()
        ? Math.round(parseFloat(values.knownAreaText.replace(',', '.')) * 10) / 10
        : undefined,
});

/** Create a level: same options as iOS's "Redigera våning" create flow. */
const CreateLevelModal: React.FC<CreateLevelModalProps> = ({
    show, onHide, onSubmit, existingNumbers, submitting = false
}) => {
    const schema = useMemo(() => Yup.object({
        name: Yup.string().trim().max(100, 'Max 100 tecken'),
        numberText: Yup.string()
            .trim()
            .required('Våningsnummer krävs')
            .test('is-integer', 'Våningsnummer måste vara ett heltal', v => v !== undefined && Number.isInteger(Number(v)))
            .test('is-unique', 'Detta våningsnummer används redan', v => v === undefined || !existingNumbers.includes(Number(v))),
        ceilingHeightMm: Yup.string().test(
            'positive',
            'Takhöjd måste vara ett positivt antal millimeter',
            v => !v?.trim() || (Number.isFinite(Number(v)) && Number(v) > 0)
        ),
        knownAreaText: Yup.string().test(
            'positive-area',
            'Känd yta måste vara ett positivt antal kvadratmeter',
            v => !v?.trim() || (Number.isFinite(parseFloat(v.replace(',', '.'))) && parseFloat(v.replace(',', '.')) > 0)
        ),
    }), [existingNumbers]);

    const handleSubmit = async (values: CreateLevelFormValues, helpers: FormikHelpers<CreateLevelFormValues>) => {
        try {
            await onSubmit(toInput(values));
            helpers.resetForm();
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte skapa våningsplanet');
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Ny våning</Modal.Title>
            </Modal.Header>
            <Formik<CreateLevelFormValues>
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, status, handleChange, handleBlur, handleSubmit: submitForm, setStatus }) => (
                    <Form noValidate onSubmit={submitForm}>
                        <Modal.Body>
                            {status && (
                                <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Eget namn</Form.Label>
                                <Form.Control
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={touched.name && !!errors.name}
                                    autoFocus
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Våningsnummer *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    name="numberText"
                                    value={values.numberText}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={touched.numberText && !!errors.numberText}
                                />
                                <Form.Control.Feedback type="invalid">{errors.numberText}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Takhöjd (mm)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    name="ceilingHeightMm"
                                    value={values.ceilingHeightMm}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={touched.ceilingHeightMm && !!errors.ceilingHeightMm}
                                    placeholder="Valfritt"
                                />
                                <Form.Control.Feedback type="invalid">{errors.ceilingHeightMm}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-1">
                                <Form.Label>Känd yta (m²)</Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    name="knownAreaText"
                                    value={values.knownAreaText}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={touched.knownAreaText && !!errors.knownAreaText}
                                    placeholder="Valfritt, t.ex. 45,5"
                                />
                                <Form.Control.Feedback type="invalid">{errors.knownAreaText}</Form.Control.Feedback>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={onHide} disabled={submitting}>Avbryt</Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? <><Spinner size="sm" className="me-2" />Sparar...</> : 'Skapa'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default CreateLevelModal;
